import { useEffect, useMemo, useRef } from "react";
import type { Asset, Clip } from "../timeline/EditorStore";
import { isWebCodecsPreviewEnabled } from "./toggles";
import {
  isLongFormMediaDuration,
  shouldDeferLongVideoBackgroundWork,
} from "../utils/longVideoRuntime";
import type { DecoderPool } from "./WebCodecsDecoderPool";

/**
 * Pin/unpin the selected clip in the DecoderPool so it is guaranteed a
 * decoder slot.  All engine lifecycle (creation, disposal, canvas-map
 * population, seeking) is handled by the pool — this hook only manages
 * the pin state.
 */
export function useWebCodecsSelectedClipPreview(params: {
  clips: Clip[];
  assets: Asset[];
  selection: { clipIds: string[] };
  playheadSec: number;
  isPlaying: boolean;
  runtime: React.MutableRefObject<{
    videoMap: Map<string, HTMLVideoElement>;
    webCodecsCanvasMap: Map<string, HTMLCanvasElement>;
  }>;
  layerRef: React.RefObject<any>;
  pool?: DecoderPool | null;
}) {
  const { clips, assets, selection, pool = null } = params;

  const enabled = isWebCodecsPreviewEnabled();

  // ── Resolve the eligible selected clip (same guards as before) ──────
  const selected = useMemo(() => {
    const selectedClipId = selection?.clipIds?.[0];
    if (!enabled) return null;
    if (!selectedClipId) return null;

    const clip = (clips || []).find((c) => c.id === selectedClipId);
    if (!clip) return null;
    const elementId =
      typeof (clip as any).elementId === "string"
        ? ((clip as any).elementId as string)
        : "";
    if (!elementId) return null;

    const asset = (assets || []).find((a) => a.id === clip.assetId);
    if (
      !asset ||
      asset.type !== "video" ||
      typeof asset.src !== "string" ||
      !asset.src
    )
      return null;
    if (isLongFormMediaDuration(asset.duration)) {
      return null;
    }
    if (
      shouldDeferLongVideoBackgroundWork({
        durationSec: asset.duration,
        previewUrl: asset.previewUrl,
        srcUrl: asset.src,
      })
    ) {
      return null;
    }

    return { clip, asset, elementId };
  }, [assets, clips, enabled, selection?.clipIds]);

  const resolvedElementId = selected?.elementId ?? null;

  // ── Pin / unpin the selected clip in the pool ───────────────────────
  const prevPinnedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pool) {
      // No pool yet — ensure we're clean.
      if (prevPinnedRef.current) {
        prevPinnedRef.current = null;
      }
      return;
    }

    const nextId = resolvedElementId;

    if (nextId && nextId !== prevPinnedRef.current) {
      // New selection — unpin old, pin new.
      if (prevPinnedRef.current) pool.unpinClip(prevPinnedRef.current);
      pool.pinClip(nextId);
      prevPinnedRef.current = nextId;
    } else if (!nextId && prevPinnedRef.current) {
      // Deselected — unpin.
      pool.unpinClip(prevPinnedRef.current);
      prevPinnedRef.current = null;
    }

    return () => {
      if (prevPinnedRef.current) {
        pool.unpinClip(prevPinnedRef.current);
        prevPinnedRef.current = null;
      }
    };
  }, [pool, resolvedElementId]);

  // ── Return values (backward-compatible shape) ───────────────────────
  const skipElementIds = useMemo(() => {
    return resolvedElementId
      ? new Set<string>([resolvedElementId])
      : new Set<string>();
  }, [resolvedElementId]);

  return {
    enabled,
    skipElementIds,
    activeElementId: resolvedElementId,
  };
}
